import { z } from 'zod';

// Admin companies API DTOs (snake_case wire contract). The backend hand-shapes
// drizzle/ClickHouse rows into these at the response boundary; the frontend
// reads them. Sharing the schema makes a drift between the two a compile error.
//
// JSONB blobs (`attributes`, `traits`, `metadata`) are passed through as
// opaque objects — customer-supplied keys are never renamed. Timestamps are
// ISO strings on the wire (the backend serializes its Date columns).
const jsonObject = z.record(z.string(), z.unknown());

// A company row in the list (top-companies over a window).
export const companySummarySchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  domain: z.string().nullable(),
  attributes: jsonObject,
  enrichment_status: z.string().nullable(),
  events: z.number(),
  users: z.number(),
  last_seen: z.string().nullable(),
  // Per-company revenue over the window (major units, in the response's
  // `currency`). Defaults to 0 for orgs/projects without revenue analytics or
  // pre-rollup history (see the 0007 MV backfill caveat).
  revenue: z.number().default(0),
  // How this company first arrived. Derived, never stored.
  acquisition_channel: z.string().nullable().default(null),
});
export type CompanySummary = z.infer<typeof companySummarySchema>;

export const companiesListResponseSchema = z.object({
  companies: z.array(companySummarySchema),
  cap: z.number().nullable(),
  // The org reporting currency `revenue` is in; older backends omit it.
  currency: z.string().optional(),
});
export type CompaniesListResponse = z.infer<typeof companiesListResponseSchema>;

// Company detail + windowed metrics.
export const companyDetailSchema = z.object({
  company: z.object({
    id: z.string(),
    name: z.string().nullable(),
    domain: z.string().nullable(),
    attributes: jsonObject,
    links: z.array(z.object({ url: z.string(), label: z.string().optional() })).default([]),
    enrichment_status: z.string().nullable(),
    enriched_at: z.string().nullable(),
    source_provider: z.string().nullable(),
    group_id: z.string().nullable(),
  }),
  metrics: z.object({
    events: z.number(),
    users: z.number(),
    last_seen: z.string().nullable(),
    members: z.number(),
    // Per-company revenue over the window (major units, in `currency`).
    revenue: z.number().default(0),
    // The org reporting currency `revenue` is in; older backends omit it.
    currency: z.string().optional(),
  }),
});
export type CompanyDetail = z.infer<typeof companyDetailSchema>;

// An identified member identity of a company.
export const companyMemberSchema = z.object({
  identity_id: z.string(),
  primary_user_id: z.string().nullable(),
  last_seen_at: z.string(),
  role: z.string().nullable(),
  is_primary: z.boolean(),
  traits: jsonObject,
});
export type CompanyMember = z.infer<typeof companyMemberSchema>;

export const companyTopUserSchema = z.object({
  resolved_identifier: z.string(),
  // The stitched identity id when the identifier resolves to one (a UUID), else
  // null. Drives the link to the identity page; null = a raw user/anonymous id
  // with no identity, which isn't linkable.
  identity_id: z.string().nullable(),
  // Friendly display label: primary user id, else an email trait, else the raw
  // identifier — mirrors how company Members render.
  label: z.string(),
  events: z.number(),
  last_seen: z.string().nullable(),
});
export type CompanyTopUser = z.infer<typeof companyTopUserSchema>;

// An event in a company's activity feed. Passed through from the events
// service, so camelCase fields here mirror that shape (not re-cased).
export const companyEventSchema = z.object({
  id: z.string(),
  name: z.string(),
  message: z.string().nullable().optional(),
  userId: z.string().nullable().optional(),
  project: z.string().nullable().optional(),
  createdAt: z.string(),
  metadata: jsonObject.nullable().optional(),
});
export type CompanyEvent = z.infer<typeof companyEventSchema>;

export const companyEventsResponseSchema = z.object({
  events: z.array(companyEventSchema),
  next_cursor: z.string().nullable(),
});
export type CompanyEventsResponse = z.infer<typeof companyEventsResponseSchema>;

// ── Canonical company attributes ───────────────────────────────────────
// One schema shared by manual edits AND the enrichment providers, so the two
// write the SAME keys (no duplicate display rows / drift). Controlled-vocab
// fields (employee `size`, `funding_stage`, `company_type`) use these enums so
// manual values match what the normalizers map provider data into; `size`
// mirrors the PDL Free Company Dataset buckets so seeded data already aligns.
export const EMPLOYEE_BANDS = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1000',
  '1001-5000',
  '5001-10000',
  '10001+',
] as const;
export type EmployeeBand = (typeof EMPLOYEE_BANDS)[number];

export const FUNDING_STAGES = [
  'Bootstrapped',
  'Pre-Seed',
  'Seed',
  'Series A',
  'Series B',
  'Series C',
  'Series D+',
  'Public',
  'Acquired',
] as const;
export type FundingStage = (typeof FUNDING_STAGES)[number];

export const COMPANY_TYPES = [
  'Private',
  'Public',
  'Nonprofit',
  'Government',
  'Education',
] as const;
export type CompanyType = (typeof COMPANY_TYPES)[number];

// Manual edit of a company's profile (attributes). All fields optional; only
// provided keys are merged into the company's `attributes` jsonb (empty
// strings/arrays stripped client-side). Keys are the canonical attribute set
// — the existing enrichment keys (industry/size/country/region/locality/
// website/linkedin_url) PLUS richer standard fields. `size` is an employee
// band; `locality` = city. `tech_stack` is free-form (any tech). Stored loosely
// (strings) so existing/provider values never fail validation; the form guides
// input with selects for the band fields.
export const updateCompanyProfileRequestSchema = z.object({
    // Org-curated links (full replace, cap 10); labels/icons derive from host.
    links: z
      .array(z.object({ url: z.string().min(1).max(512), label: z.string().max(60).optional() }))
      .max(10)
      .optional(),
  description: z.string().max(2000).optional(),
  industry: z.string().max(255).optional(),
  size: z.string().max(64).optional(),
  founded: z.string().max(10).optional(),
  company_type: z.string().max(64).optional(),
  funding_stage: z.string().max(64).optional(),
  tech_stack: z.array(z.string().max(120)).max(100).optional(),
  website: z.string().max(512).optional(),
  linkedin_url: z.string().max(512).optional(),
  twitter_url: z.string().max(512).optional(),
  crunchbase_url: z.string().max(512).optional(),
  country: z.string().max(255).optional(),
  region: z.string().max(255).optional(),
  locality: z.string().max(255).optional(),
});
export type UpdateCompanyProfileRequest = z.infer<
  typeof updateCompanyProfileRequestSchema
>;

// ── Attributes normalization ────────────────────────────────────────────
// Enrichment providers emit divergent key vocabularies: internal/PDL writes the
// canonical keys, Cleanlist writes `industries`/`employee_count_range`/
// `hq_location` + lowercase enum values (`funding_stage: "seed"`). The display grid and edit form expect ONE
// canonical set, so every attributes blob is normalized — on read (API
// response shaping) and on write (enrich + manual edit). Pure, lenient, and
// non-destructive: unknown keys pass through untouched (customer keys are never
// renamed), values are never invented, and a mapped source key is dropped so the
// grid shows one row per fact, not two.
const isBlank = (v: unknown): boolean =>
  v === null ||
  v === undefined ||
  (typeof v === 'string' && v.trim() === '') ||
  (Array.isArray(v) && v.length === 0);

// Title-case a free-text token ("non profit" → "Non Profit") — the controlled-
// vocab fallback when no alias matches.
const titleCaseWords = (s: string): string =>
  s
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

// Lowercase + collapse `_`/`-`/whitespace to single spaces, for alias lookup.
const aliasKey = (s: string): string =>
  s.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');

const FUNDING_STAGE_ALIASES: Record<string, FundingStage> = {
  bootstrapped: 'Bootstrapped',
  'pre seed': 'Pre-Seed',
  preseed: 'Pre-Seed',
  seed: 'Seed',
  'series a': 'Series A',
  'series b': 'Series B',
  'series c': 'Series C',
  'series d': 'Series D+',
  'series e': 'Series D+',
  'series f': 'Series D+',
  'series d+': 'Series D+',
  'late stage': 'Series D+',
  public: 'Public',
  ipo: 'Public',
  acquired: 'Acquired',
  acquisition: 'Acquired',
  'm&a': 'Acquired',
};

const COMPANY_TYPE_ALIASES: Record<string, CompanyType> = {
  private: 'Private',
  public: 'Public',
  nonprofit: 'Nonprofit',
  'non profit': 'Nonprofit',
  government: 'Government',
  govt: 'Government',
  education: 'Education',
  educational: 'Education',
};

const EMPLOYEE_BAND_SET = new Set<string>(EMPLOYEE_BANDS);

// Map a vendor employee count (number) or range (string) to an EMPLOYEE_BANDS
// value, else null. Cleanlist/PDL ranges (`"11-50"`, `"10001+"`) match directly
// once commas/whitespace are stripped (`"5,001-10,000"` → `"5001-10000"`).
const toEmployeeBand = (v: unknown): EmployeeBand | null => {
  if (typeof v === 'number' && Number.isFinite(v)) {
    if (v <= 10) return '1-10';
    if (v <= 50) return '11-50';
    if (v <= 200) return '51-200';
    if (v <= 500) return '201-500';
    if (v <= 1000) return '501-1000';
    if (v <= 5000) return '1001-5000';
    if (v <= 10000) return '5001-10000';
    return '10001+';
  }
  if (typeof v === 'string') {
    const cleaned = v.replace(/,/g, '').replace(/\s+/g, '');
    if (EMPLOYEE_BAND_SET.has(cleaned)) return cleaned as EmployeeBand;
  }
  return null;
};

/**
 * Normalize a attributes blob into the canonical key set + controlled-vocab
 * values. Idempotent (safe to run on read AND write); returns a new object.
 */
export const normalizeAttributes = (
  raw: Record<string, unknown> | null | undefined,
): Record<string, unknown> => {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, unknown> = { ...raw };

  // Set `key` only when it's currently empty and `value` isn't (never clobber a
  // value that's already canonical).
  const fillIfBlank = (key: string, value: unknown): void => {
    if (!isBlank(value) && isBlank(out[key])) out[key] = value;
  };

  // industries (array | comma string) → industry
  if (!isBlank(out.industries)) {
    const v = out.industries;
    fillIfBlank(
      'industry',
      Array.isArray(v) ? v.filter(Boolean).join(', ') : String(v),
    );
    delete out.industries;
  }

  // employee_count_range / employee_count → size (band)
  if (!isBlank(out.employee_count_range)) {
    fillIfBlank('size', toEmployeeBand(out.employee_count_range) ?? out.employee_count_range);
    delete out.employee_count_range;
  }
  if (!isBlank(out.employee_count)) {
    const band = toEmployeeBand(out.employee_count);
    if (band) fillIfBlank('size', band);
    delete out.employee_count;
  }

  // hq_location "City, Region, Country" → locality/region/country (right-anchored)
  if (typeof out.hq_location === 'string' && out.hq_location.trim()) {
    const parts = out.hq_location
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length >= 1) fillIfBlank('country', parts[parts.length - 1]);
    if (parts.length >= 2) fillIfBlank('region', parts[parts.length - 2]);
    if (parts.length >= 3) fillIfBlank('locality', parts[parts.length - 3]);
    delete out.hq_location;
  }

  // Controlled-vocab values → canonical (alias match, else Title Case as-is).
  if (typeof out.funding_stage === 'string' && out.funding_stage.trim()) {
    out.funding_stage =
      FUNDING_STAGE_ALIASES[aliasKey(out.funding_stage)] ??
      titleCaseWords(out.funding_stage);
  }
  if (typeof out.company_type === 'string' && out.company_type.trim()) {
    out.company_type =
      COMPANY_TYPE_ALIASES[aliasKey(out.company_type)] ??
      titleCaseWords(out.company_type);
  }

  // Canonicalize a vendor-format value already sitting in `size`.
  if (!isBlank(out.size)) {
    const band = toEmployeeBand(out.size);
    if (band) out.size = band;
  }

  // founded as a number (PDL) → string (the form/schema treat it as a string).
  if (typeof out.founded === 'number') out.founded = String(out.founded);

  return out;
};

// The primary company of an identity (user→company connected view), or null.
export const companyForIdentitySchema = z
  .object({
    id: z.string(),
    name: z.string().nullable(),
    domain: z.string().nullable(),
  })
  .nullable();
export type CompanyForIdentity = z.infer<typeof companyForIdentitySchema>;

// The Companies overview above the list: activity this period against the one before, coverage,
// who is visiting, how they first arrived, and the Target Accounts watchlist.
export type CompanyOverviewRow = { label: string; count: number };
export type CompanyOverview = {
  total: number;
  previousTotal: number;
  newCompanies: {
    total: number;
    previousTotal: number;
    perDay: { date: string; count: number }[];
  };
  // Active companies that have industry, size and country.
  enriched: number;
  firmographics: Record<'industry' | 'size' | 'country', CompanyOverviewRow[]>;
  // First-touch channel; null when a company has no recorded first touch.
  arrived: { channel: string | null; count: number }[];
  targets: {
    listed: number;
    active: number;
    previousActive: number;
    // Listed companies active each day, this period and the one before.
    perDay: { date: string; count: number }[];
    previousPerDay: { date: string; count: number }[];
    accounts: {
      id: string;
      companyId: string | null;
      name: string | null;
      domain: string | null;
      lastSeen: string | null;
    }[];
  };
};
